import { toConditionEntry, isV2Format, getDependencyTaskId, getDependencyBranch } from './formats';
import { normalizeToV2 } from './convert';

/**
 * Convert any stored format to UI blocks format.
 * Returns { blocks, outerOperators } where:
 *   blocks = [{ conditions: [{ taskId, branch }], innerOperator: 'AND'|'OR' }, ...]
 *   outerOperators = [null, 'AND'|'OR', ...]
 */
export function toUIFormat(unlockConditions) {
  const v2 = normalizeToV2(unlockConditions);

  if (v2.blocks.length === 0) return { blocks: [], outerOperators: [] };

  const blocks = v2.blocks.map(b => ({
    conditions: b.conditions.map(c => toConditionEntry(c)),
    innerOperator: b.innerOperator || 'AND',
  }));

  const outerOperators = v2.blocks.map(b => b.outerOperator || null);

  return { blocks, outerOperators };
}

/**
 * Convert UI blocks format back to v2 storage format.
 */
export function fromUIFormat(blocks, outerOperators) {
  const v2Blocks = blocks.map((block, i) => ({
    conditions: block.conditions.filter(c => c.taskId).map(c => toConditionEntry(c)),
    innerOperator: block.innerOperator || 'AND',
    outerOperator: i === 0 ? null : (outerOperators[i] || 'AND'),
  })).filter(b => b.conditions.length > 0);

  if (v2Blocks.length > 0) {
    v2Blocks[0] = { ...v2Blocks[0], outerOperator: null };
  }

  return { version: 2, blocks: v2Blocks };
}

/**
 * @deprecated Use getEffectiveConditionsV2 instead
 */
export function getEffectiveConditions(task) {
  if (task.unlockConditions && isV2Format(task.unlockConditions)) {
    return task.unlockConditions;
  }
  if (task.unlockConditions && Array.isArray(task.unlockConditions) && task.unlockConditions.length > 0) {
    return task.unlockConditions;
  }
  return convertDependsOnToConditions(task.dependsOn);
}

/**
 * @deprecated Legacy v1 helper — converts dependsOn to flat array
 */
export function convertDependsOnToConditions(dependsOn) {
  if (!dependsOn || !Array.isArray(dependsOn) || dependsOn.length === 0) {
    return [];
  }
  const flat = [];
  for (let i = 0; i < dependsOn.length; i++) {
    const dep = dependsOn[i];
    const taskId = getDependencyTaskId(dep);
    const branch = getDependencyBranch(dep);
    if (!taskId) continue;
    flat.push({ taskId, branch, operator: i === 0 ? null : 'AND' });
  }
  return flat;
}

/**
 * @deprecated Use toUIFormat instead
 */
export function migrateConditions(conditions) {
  if (!conditions || (Array.isArray(conditions) && conditions.length === 0)) return [];
  if (isV2Format(conditions)) {
    const { blocks } = toUIFormat(conditions);
    return blocks;
  }
  if (conditions[0]?.tasks) return conditions;

  const options = [];
  let currentOption = { tasks: [] };
  for (let i = 0; i < conditions.length; i++) {
    const condition = conditions[i];
    if (!condition.taskId) continue;
    const taskEntry = { taskId: condition.taskId, branch: condition.branch || null };
    if (i === 0 || condition.operator === 'AND') {
      currentOption.tasks.push(taskEntry);
    } else if (condition.operator === 'OR') {
      if (currentOption.tasks.length > 0) options.push(currentOption);
      currentOption = { tasks: [taskEntry] };
    }
  }
  if (currentOption.tasks.length > 0) options.push(currentOption);
  return options;
}

/**
 * @deprecated Use fromUIFormat instead
 */
export function flattenConditions(options) {
  const flat = [];
  for (let oi = 0; oi < options.length; oi++) {
    const option = options[oi];
    const tasks = option.tasks || option.conditions || [];
    for (let ti = 0; ti < tasks.length; ti++) {
      const taskEntry = tasks[ti];
      const taskId = typeof taskEntry === 'string' ? taskEntry : taskEntry.taskId;
      const branch = typeof taskEntry === 'string' ? null : taskEntry.branch;
      if (!taskId) continue;
      const condition = { taskId };
      if (branch) condition.branch = branch;
      if (oi === 0 && ti === 0) condition.operator = null;
      else if (ti === 0) condition.operator = 'OR';
      else condition.operator = 'AND';
      flat.push(condition);
    }
  }
  return flat;
}
